import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Flame, Users, MapPin, Play, TrendingUp, Loader2, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import VideoPlayer from '@/components/VideoPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsBuzzing = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [buzzing, setBuzzing] = useState(null);
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState({ lat: 33.4936, lng: -117.1484 }); // Default to Temecula
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchBars();
    getUserLocation();
  }, []);

  const fetchBars = async () => {
    try {
      const response = await axios.get(`${API}/bars`);
      setBars(response.data);
    } catch (e) {
      console.error('Failed to fetch bars:', e);
    }
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          fetchBuzzing(location);
        },
        () => {
          // Use default Temecula location if geolocation fails
          const defaultLocation = { lat: 33.4936, lng: -117.1484 };
          setUserLocation(defaultLocation);
          fetchBuzzing(defaultLocation);
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } else {
      const defaultLocation = { lat: 33.4936, lng: -117.1484 };
      setUserLocation(defaultLocation);
      fetchBuzzing(defaultLocation);
    }
  };

  const fetchBuzzing = async (location) => {
    try {
      const response = await axios.get(
        `${API}/feed/whats-buzzing?user_lat=${location.lat}&user_lng=${location.lng}&radius_miles=7`
      );
      setBuzzing(response.data);
    } catch (e) {
      console.error('Failed to fetch buzzing data:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchBuzzing(userLocation);
  };

  const getVibeStyle = (vibeColor) => {
    switch (vibeColor) {
      case 'red':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'orange':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'yellow':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default:
        return 'bg-slate-500/20 text-slate-400 border-slate-500/50';
    }
  };

  // Get profile picture URL
  const getProfilePicUrl = () => {
    if (user?.profile_picture_url) {
      return user.profile_picture_url.startsWith('/api') 
        ? `${BACKEND_URL}${user.profile_picture_url}` 
        : user.profile_picture_url;
    }
    return null;
  };

  if (loading) {
    return (
      <Card className="glass p-6 border border-purple-500/30">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
        </div>
      </Card>
    );
  }

  // Generate demo data using actual bars from database
  const generateDemoData = () => {
    if (bars.length === 0) {
      return {
        updated_at: new Date().toISOString(),
        radius_miles: 7,
        total_active_bars: 0,
        top_buzzing: []
      };
    }

    const demoStats = [
      { checkin_count: 12, female_count: 7, male_count: 5, ratio_display: '7:5', vibe: '🔥 Packed', vibe_color: 'red', buzz_score: 78.5 },
      { checkin_count: 8, female_count: 5, male_count: 3, ratio_display: '5:3', vibe: '⚡ Lively', vibe_color: 'orange', buzz_score: 62.3 },
      { checkin_count: 5, female_count: 3, male_count: 2, ratio_display: '3:2', vibe: '⚡ Lively', vibe_color: 'orange', buzz_score: 45.8 }
    ];
    const rankEmojis = ['🥇', '🥈', '🥉'];

    return {
      updated_at: new Date().toISOString(),
      radius_miles: 7,
      total_active_bars: Math.min(bars.length, 3),
      top_buzzing: bars.slice(0, 3).map((bar, index) => ({
        bar: {
          id: bar.id,
          name: bar.name,
          address: bar.address || '',
          image_url: bar.image_url
        },
        stats: {
          ...demoStats[index],
          ratio_percentage: demoStats[index].female_count / (demoStats[index].female_count + demoStats[index].male_count) * 100
        },
        vibe: demoStats[index].vibe,
        vibe_color: demoStats[index].vibe_color,
        buzz_score: demoStats[index].buzz_score,
        distance_miles: (0.3 + index * 0.2).toFixed(1),
        live_videos: [],
        rank: index + 1,
        rank_emoji: rankEmojis[index]
      }))
    };
  };

  // Use demo data when no real activity (either empty or all bars have 0 check-ins)
  const demoData = generateDemoData();
  const hasRealActivity = buzzing && buzzing.top_buzzing.length > 0 && 
    buzzing.top_buzzing.some(b => b.stats.checkin_count > 0);
  const displayData = hasRealActivity ? buzzing : demoData;
  const isPreviewMode = !hasRealActivity;

  // If still no data (bars haven't loaded), show loading state
  if (displayData.top_buzzing.length === 0) {
    return (
      <Card className="glass p-6 border border-slate-700">
        <div className="flex items-center gap-2 mb-4">
          <Flame className="w-6 h-6 text-orange-400" />
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            What's Buzzing
          </h2>
        </div>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 text-purple-400 animate-spin mr-2" />
          <span className="text-slate-400">Loading hot spots...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="glass p-6 border border-orange-500/30 fade-in">
      {/* Header with Big Profile Picture */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          {/* Big Profile Picture */}
          {getProfilePicUrl() ? (
            <img 
              src={getProfilePicUrl()}
              alt={user?.username || 'User'}
              className="w-16 h-16 rounded-full object-cover border-3 border-orange-500 shadow-lg shadow-orange-500/30"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center border-3 border-orange-400 shadow-lg shadow-orange-500/30">
              <span className="text-white text-2xl font-bold">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Flame className="w-8 h-8 text-orange-400 animate-pulse" />
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                What's Buzzing
              </h2>
              <span className="text-xs text-slate-400">
                within 7 miles {isPreviewMode && <span className="text-orange-400">(Preview)</span>}
              </span>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-slate-400 hover:text-white"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Top 3 Buzzing Bars */}
      <div className="space-y-4">
        {displayData.top_buzzing.map((item, index) => (
          <div
            key={item.bar.id}
            className={`p-4 rounded-lg border cursor-pointer transition-all hover:scale-[1.02] ${
              index === 0 
                ? 'bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/50' 
                : 'bg-slate-800/50 border-slate-700 hover:border-purple-500/50'
            }`}
            onClick={() => navigate(`/bar/${item.bar.id}`)}
          >
            <div className="flex items-start gap-4">
              {/* Rank */}
              <div className="text-3xl">{item.rank_emoji}</div>
              
              {/* Bar Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-white text-lg truncate">{item.bar.name}</h3>
                    <p className="text-sm text-slate-400 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {item.distance_miles} mi away
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getVibeStyle(item.vibe_color)}`}>
                    {item.vibe}
                  </span>
                </div>
                
                {/* Stats Row */}
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1 text-sm">
                    <Users className="w-4 h-4 text-purple-400" />
                    <span className="text-white font-medium">{item.stats.checkin_count}</span>
                    <span className="text-slate-400">people</span>
                  </div>
                  {item.stats.ratio_display !== 'N/A' && (
                    <div className="flex items-center gap-1 text-sm">
                      <span className="text-pink-400">♀</span>
                      <span className="text-white font-medium">{item.stats.ratio_display}</span>
                      <span className="text-blue-400">♂</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-sm">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 font-medium">{item.buzz_score}</span>
                    <span className="text-slate-400">buzz</span>
                  </div>
                </div>

                {/* Live Videos Preview */}
                {item.live_videos && item.live_videos.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Play className="w-4 h-4 text-red-400" />
                      <span className="text-xs text-red-400 font-medium">LIVE</span>
                      <span className="text-xs text-slate-400">{item.live_videos.length} video(s)</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {item.live_videos.slice(0, 3).map((video, vidIdx) => (
                        <div 
                          key={vidIdx} 
                          className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative group"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <video
                            src={`${process.env.REACT_APP_BACKEND_URL}${video.url}`}
                            className="w-full h-full object-cover"
                            muted
                            loop
                            playsInline
                            onMouseEnter={(e) => e.target.play()}
                            onMouseLeave={(e) => e.target.pause()}
                          />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {displayData.total_active_bars} bars with activity nearby
        </p>
        <p className="text-xs text-slate-500">
          Updated {formatDistanceToNow(new Date(displayData.updated_at), { addSuffix: true })}
        </p>
      </div>
    </Card>
  );
};

export default WhatsBuzzing;
